import { defineComponent as _defineComponent } from 'vue'
import { pollTailwindClasses, toOzBoxColor } from '@@/bits/post_color'
import { isPostScheduled } from '@@/bits/post_state'
import { useSurfaceStore } from '@@/pinia/surface'
import type { Poll, Post, PostColor, User } from '@@/types'
import OzBox, { OzBoxColors } from 'library/v4/components/OzBox.vue'
import { storeToRefs } from 'pinia'
import { useSurfacePermissionsStore } from 'pinia/surface_permissions'
import { useSurfacePostsStore } from 'pinia/surface_posts'
import { useWindowSizeStore } from 'pinia/window_size'
import { computed, onMounted, ref, toRefs } from 'vue'
import SurfacePostPollBottomSection from 'vuecomponents/SurfacePostPollBottomSection.vue'
import SurfacePostPollChoice from 'vuecomponents/SurfacePostPollChoice.vue'
import SurfacePostPollQuestion from 'vuecomponents/SurfacePostPollQuestion.vue'
import SurfacePostPollResult from 'vuecomponents/SurfacePostPollResult.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'SurfacePostPoll',
  props: {
    poll: null,
    pollCaption: { default: '' },
    post: null,
    backgroundColor: { default: undefined },
    user: null,
    darkMode: { type: [Boolean, String], default: 'auto' },
    displayMode: { default: 'default' },
    xOverlay: { type: Boolean, default: false }
  },
  emits: ["dblclick", "click", "enter", "load"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'dblclick', event: MouseEvent): void
  (e: 'click', event: MouseEvent): void
  (e: 'enter', event: KeyboardEvent): void
  (e: 'load')
}), expose: any, slots: any, attrs: any }) {

const props = __props as { poll: Poll, pollCaption: string, post: Post, backgroundColor: PostColor, user: User, darkMode: boolean | 'auto', displayMode: 'expanded' | 'default' | 'slideshow', xOverlay: boolean }





const { darkMode, pollCaption, poll, displayMode, xOverlay } = toRefs(props)

const isDisplayModeDefault = computed(() => displayMode.value === 'default')
const isDisplayModeExpanded = computed(() => displayMode.value === 'expanded')
const isDisplayModeSlideshow = computed(() => displayMode.value === 'slideshow')
const isDisplayModeExpandedOrSlideshow = computed(() => isDisplayModeExpanded.value || isDisplayModeSlideshow.value)
const { isSmallerThanDesktop3XL, isSmallerThanDesktop2XL } = storeToRefs(useWindowSizeStore())
const pollScaleMultiplier = computed(() => {
  if (!isSmallerThanDesktop3XL.value) {
    return 2.5
  } else if (!isSmallerThanDesktop2XL.value) {
    return 2
  } else {
    return 1
  }
})
const displayedBackgroundColor = computed<PostColor>(() =>
  props.backgroundColor === undefined ? props.post.color : props.backgroundColor,
)
const pollColors = computed(() =>
  pollTailwindClasses(isDisplayModeExpandedOrSlideshow.value ? null : displayedBackgroundColor.value),
)
const { isStream } = storeToRefs(useSurfaceStore())
const { postPollVote } = useSurfacePostsStore()
const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const isPostAuthor = computed(() => props.post.author_id === props.user.id)
const totalVotesArray = computed(() => {
  return poll.value.poll_choices.map((choice) => choice.total_votes)
})
const totalVotesCount = computed(() => {
  return totalVotesArray.value.reduce((a, b) => a + b, 0)
})
const highestVoteTotal = computed(() => Math.max(...totalVotesArray.value))
const isPollClosed = computed(() => poll.value.closed_at != null)
const pollResults = computed(() => {
  return poll.value.poll_choices.map((pollChoice) => {
    return {
      ...pollChoice,
      percentage: totalVotesCount.value === 0 ? 0 : (pollChoice.total_votes / totalVotesCount.value) * 100,
      pollWinner:
        pollChoice.total_votes === highestVoteTotal.value && isPollClosed.value && pollChoice.total_votes !== 0,
    }
  })
})

const didCurrentUserVoteForPoll = computed(() => {
  return poll.value.user_voted
})

const hasDecimalVoteRatio = computed(() => pollResults.value.some((pollChoice) => pollChoice.percentage % 1 !== 0))
const selectedPollChoiceId = ref(-1)
const isShowingPollResults = ref(false)
const isVoting = ref(false)
const isInteractionsDisabled = computed(() => isPostScheduled(props.post))

const bottomSectionHeight = ref(isDisplayModeExpandedOrSlideshow.value ? 48 : 40)
const pollResultsContainer = ref()
const pollScrollableContainer = ref()
const togglePollResults = () => {
  isShowingPollResults.value = !isShowingPollResults.value

  if (isShowingPollResults.value) {
    const pollResultsContainerTop = pollResultsContainer.value.offsetTop
    pollScrollableContainer.value.scrollTo({ top: pollResultsContainerTop, behavior: 'smooth' })
  }
}

const vote = async () => {
  isVoting.value = true
  await postPollVote({
    wishId: props.post.id!,
    pollId: poll.value.poll_id,
    pollChoiceIds: [selectedPollChoiceId.value],
  })
  isVoting.value = false
}

const handleBottomSectionResize = ({ width, height }) => {
  bottomSectionHeight.value = height
}

onMounted(() => {
  if (isDisplayModeSlideshow.value) {
    emit('load')
  }
})

return { __sfc: true,emit, props, darkMode, pollCaption, poll, displayMode, xOverlay, isDisplayModeDefault, isDisplayModeExpanded, isDisplayModeSlideshow, isDisplayModeExpandedOrSlideshow, isSmallerThanDesktop3XL, isSmallerThanDesktop2XL, pollScaleMultiplier, displayedBackgroundColor, pollColors, isStream, postPollVote, canIModerate, isPostAuthor, totalVotesArray, totalVotesCount, highestVoteTotal, isPollClosed, pollResults, didCurrentUserVoteForPoll, hasDecimalVoteRatio, selectedPollChoiceId, isShowingPollResults, isVoting, isInteractionsDisabled, bottomSectionHeight, pollResultsContainer, pollScrollableContainer, togglePollResults, vote, handleBottomSectionResize, toOzBoxColor, OzBox, OzBoxColors, SurfacePostPollBottomSection, SurfacePostPollChoice, SurfacePostPollQuestion, SurfacePostPollResult }
}

})